
import type { RouteObject } from 'react-router-dom';
import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

const HomePage = lazy(() => import('../pages/home/page'));
const ExplorePage = lazy(() => import('../pages/explore/page'));
const ReleasesPage = lazy(() => import('../pages/releases/page'));
const ReleaseDetailPage = lazy(() => import('../pages/releases/detail/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const SignUpPage = lazy(() => import('../pages/signup/page'));
const SignInPage = lazy(() => import('../pages/signin/page'));
const AdminPage = lazy(() => import('../pages/admin/page'));
const AdminDashboardPage = lazy(() => import('../pages/admin/dashboard/page'));
const AdminStatsPage = lazy(() => import('../pages/admin/stats/page'));
const AdminArtistsPage = lazy(() => import('../pages/admin/artists/page'));
const AdminArtistCreatePage = lazy(() => import('../pages/admin/artists/new/page'));
const AdminArtistEditPage = lazy(() => import('../pages/admin/artists/edit/page'));
const AdminArtistReleasesPage = lazy(() => import('../pages/admin/artists/releases/page'));
const AdminAccessPage = lazy(() => import('../pages/admin/access/page'));
const AdminReleasesPage = lazy(() => import('../pages/admin/releases/page'));
const AdminReleaseCreatePage = lazy(() => import('../pages/admin/releases/new/page'));
const AdminReleaseEditPage = lazy(() => import('../pages/admin/releases/edit/page'));
const AdminOrdersPage = lazy(() => import('../pages/admin/orders/page'));
const AdminDiscountsPage = lazy(() => import('../pages/admin/discounts/page'));
const AdminEarningsPage = lazy(() => import('../pages/admin/earnings/page'));
const AdminPayeesPage = lazy(() => import('../pages/admin/payees/page'));
const AdminPayoutsPage = lazy(() => import('../pages/admin/payouts/page'));
const AdminLanesPage = lazy(() => import('../pages/admin/lanes/page'));
const AdminApprovalsPage = lazy(() => import('../pages/admin/approvals/page'));
const AdminApplicationsPage = lazy(() => import('../pages/admin/applications/page'));
const AdminFeaturedArtistsPage = lazy(() => import('../pages/admin/featured-artists/page'));
const AdminFeaturedDropsPage = lazy(() => import('../pages/admin/featured-drops/page'));
const AdminUsersPage = lazy(() => import('../pages/admin/users/page'));
const AdminUserMailPage = lazy(() => import('../pages/admin/user-mail/page'));
const AdminEmailDebugPage = lazy(() => import('../pages/admin/email-debug/page'));
const ForgotPasswordPage = lazy(() => import('../pages/forgot-password/page'));
const BecomeArtistPage = lazy(() => import('../pages/artists/apply/page'));
const ArtistOnboardingPage = lazy(() => import('../pages/artist/onboarding/page'));
const LibraryPage = lazy(() => import('../pages/library/page'));
const AuthCallbackPage = lazy(() => import('../pages/auth/callback/page'));

import ArtistsPage from '../pages/artists/page';
import ArtistPage from '../pages/artist/page';
import { CartPage } from '../pages/cart/page';

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />
  },
  {
    path: '/explore',
    element: <ExplorePage />
  },
  {
    path: '/releases',
    element: <ReleasesPage />
  },
  {
    path: '/releases/:slug',
    element: <ReleaseDetailPage />
  },
  {
    path: '/artists',
    element: <ArtistsPage />
  },
  {
    path: '/artists/:slug',
    element: <ArtistPage />
  },
  {
    path: '/artist/:slug',
    element: <ArtistPage />
  },
  {
    path: '/artists/apply',
    element: <BecomeArtistPage />
  },
  {
    path: '/artist/onboarding',
    element: <ArtistOnboardingPage />
  },
  {
    path: '/about',
    element: <AboutPage />
  },
  {
    path: '/signup',
    element: <SignUpPage />
  },
  {
    path: '/signin',
    element: <SignInPage />
  },
  {
    path: '/login',
    element: <SignInPage />
  },
  {
    path: '/admin',
    element: <AdminPage />,
    children: [
      {
        index: true,
        element: <AdminDashboardPage />
      },
      {
        path: 'stats',
        element: <AdminStatsPage />
      },
      {
        path: 'approvals',
        element: <AdminApprovalsPage />
      },
      {
        path: 'applications',
        element: <AdminApplicationsPage />
      },
      {
        path: 'artists',
        element: <AdminArtistsPage />
      },
      {
        path: 'artists/new',
        element: <AdminArtistCreatePage />
      },
      {
        path: 'artists/:artistId/edit',
        element: <AdminArtistEditPage />
      },
      {
        path: 'artists/:artistId/releases',
        element: <AdminArtistReleasesPage />
      },
      {
        path: 'featured-artists',
        element: <AdminFeaturedArtistsPage />
      },
      {
        path: 'featured-drops',
        element: <AdminFeaturedDropsPage />
      },
      {
        path: 'users',
        element: <AdminUsersPage />
      },
      {
        path: 'user-mail',
        element: <AdminUserMailPage />
      },
      {
        path: 'access',
        element: <AdminAccessPage />
      },
      {
        path: 'releases',
        element: <AdminReleasesPage />
      },
      {
        path: 'releases/new',
        element: <AdminReleaseCreatePage />
      },
      {
        path: 'releases/:releaseId/edit',
        element: <AdminReleaseEditPage />
      },
      {
        path: 'discounts',
        element: <AdminDiscountsPage />
      },
      {
        path: 'orders',
        element: <AdminOrdersPage />
      },
      {
        path: 'earnings',
        element: <AdminEarningsPage />
      },
      {
        path: 'payees',
        element: <AdminPayeesPage />
      },
      {
        path: 'payouts',
        element: <AdminPayoutsPage />
      },
      {
        path: 'lanes',
        element: <AdminLanesPage />
      },
      {
        path: 'email-debug',
        element: <AdminEmailDebugPage />
      }
    ]
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />
  },
  {
    path: '/cart',
    element: <CartPage />
  },
  {
    path: '/library',
    element: <LibraryPage />
  },
  {
    path: '/auth/callback',
    element: <AuthCallbackPage />
  },
  {
    path: '*',
    element: <Navigate to="/" replace />
  }
];

export default routes;
